import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Tooltip,
  Box,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  PictureAsPdf as PictureAsPdfIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

const ITEM_HEIGHT_ESTIMATE = 220;

/**
 * Memoized test card for My Tests list. Prevents re-renders when parent state changes
 * but this test's props are unchanged.
 */
const TestCard = memo(function TestCard({
  test,
  studentId,
  activeTab,
  onOpenResults,
  onOpenUpload,
  onStartTest,
  onDownloadWorksheet,
  hasWorksheet,
}) {
  const navigate = useNavigate();

  const isSelfAssigned = test.assigned_by_id != null && parseInt(test.assigned_by_id) === parseInt(studentId);
  const assignedByText = isSelfAssigned
    ? 'Assigned by self'
    : (test.assigned_by_first_name
        ? `Assigned by ${test.assigned_by_first_name}`
        : (test.assigned_by_email
            ? `Assigned by ${test.assigned_by_email.split('@')[0]}`
            : ''));
  const topicsText = Array.isArray(test.topics_list) && test.topics_list.length > 0
    ? test.topics_list.join(', ')
    : '';
  const subtitle = [assignedByText, topicsText].filter(Boolean).join(' • ') || 'No description available';

  return (
    <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="div">
              {test.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
            <Box sx={{ display: 'flex', mt: 1, gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`${test.totalQuestions || 0} Qs`}
                size="small"
                variant="outlined"
              />
              {Array.isArray(test.topics_list) && test.topics_list.length > 0 && (
                <Tooltip title={test.topics_list.join(', ')}>
                  <Chip
                    label={`${test.topics_list.length} Topics`}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              <Typography variant="caption" sx={{ mr: 1 }}>Basic: {test.percent_basic}%</Typography>
              <Typography variant="caption" sx={{ mr: 1 }}>Adv: {test.percent_advanced}%</Typography>
              {test.has_submitted_attempt ? (
                <>
                  <Typography variant="caption" sx={{ mr: 1, color: 'success.main' }}>
                    Completed: {test.completedDate ? new Date(test.completedDate).toLocaleDateString() : (test.displayDate ? new Date(test.displayDate).toLocaleDateString() : '')}
                  </Typography>
                  {test.dueDate && (
                    <Typography variant="caption" sx={{ mr: 1, color: new Date(test.dueDate) < new Date() ? 'error.main' : 'text.secondary' }}>
                      Due: {new Date(test.dueDate).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              ) : (
                test.dueDate && (
                  <Chip
                    label={`Due: ${new Date(test.dueDate).toLocaleDateString()}`}
                    size="small"
                    variant="outlined"
                    color={new Date(test.dueDate) < new Date() ? 'error' : 'default'}
                  />
                )
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, flexShrink: 0, gap: 0.5 }}>
            <Chip
              label={
                test.status === 'in-progress'
                  ? 'In Progress'
                  : test.has_submitted_attempt
                    ? 'Completed'
                    : 'Not Started'
              }
              size="small"
              color={
                test.status === 'in-progress'
                  ? 'primary'
                  : test.has_submitted_attempt
                    ? 'success'
                    : 'default'
              }
              variant={test.has_submitted_attempt ? 'outlined' : (test.status === 'not-started' ? 'outlined' : 'filled')}
            />
            {hasWorksheet && (
              <Chip
                icon={<UploadFileIcon sx={{ fontSize: '0.85rem !important' }} />}
                label="Worksheet Uploaded"
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
            {test.status === 'in-progress' && (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 150 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={test.progress || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {test.progress || 0}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {test.has_submitted_attempt ? (
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenResults(test);
                }}
                startIcon={<CheckCircleIcon />}
                sx={{ backgroundColor: 'success.light', '&:hover': { backgroundColor: 'success.main' } }}
              >
                View Results
              </Button>
              {test.created_by && parseInt(test.created_by) !== parseInt(studentId) && activeTab !== 'graded-by-teacher' ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenUpload(test);
                  }}
                >
                  Upload worksheet
                </Button>
              ) : null}
              {onDownloadWorksheet && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadWorksheet(test);
                  }}
                >
                  Download Worksheet
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (test.status === 'in-progress') {
                  navigate(`/test/${test.id}`, {
                    state: { attemptId: test.attempt_id || test.attemptId },
                  });
                } else {
                  onStartTest(test.id);
                }
              }}
              startIcon={test.status === 'in-progress' ? <RefreshIcon /> : <PlayArrowIcon />}
            >
              {test.status === 'in-progress' ? 'Continue Test' : 'Start Test'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

export default TestCard;
export { ITEM_HEIGHT_ESTIMATE };
